import React, { useState, useEffect } from 'react';

// Chỉ import các phần tử nhỏ cần thiết để tối ưu Tree-shaking cho Bundle
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ContainerGrid from "./ContainerGrid";//Sử dụng Grid này thay cho Grid của MUI để nó response theo container thay vì màn hình thiết bị
import { loadBabelOnce,numeral } from "../utils";

import moment from 'moment';

// Import sẵn Google Chart để bơm vào cho User dùng nếu họ thích vẽ đồ thị riêng
import { Chart } from "react-google-charts";
import { bienDichComponent } from './bienDichJsx';//Biên dịch script JSX -> component (an toàn với hàm phụ Babel chèn đầu mã)

// =====================================================================
// COMPONENT: TRÌNH BIÊN DỊCH GIAO DIỆN BÁO CÁO TÙY CHỈNH (RUNTIME COMPILER)
// Anh em song sinh của DynamicCustomPivot, nhưng bơm dữ liệu DẠNG DANH SÁCH
// (data/rawData/columns/condition) thay vì ma trận pivot.
// =====================================================================
const DynamicCustomReport = ({
  script,       // Đoạn code React/JSX do người dùng cấu hình
  data = [],    // Dữ liệu đang hiển thị (đã qua lọc nhanh + sắp xếp của viewer)
  rawData = [], // Dữ liệu gốc do báo cáo trả về (chưa lọc nhanh)
  columns = [], // Cấu hình cột (field, label, type...)
  condition,    // Điều kiện lọc đang áp dụng của báo cáo
  context,      // App context (Token, APIs, alert...)
  report        // Tiện ích: { refresh, openReport, openEdit, totalRecords, hasNextPage, loadMore }
}) => {
  const [CompilerConfig, setCompilerConfig] = useState({ Component: null, MUI: null, error: null, isLoading: true });

  useEffect(() => {
    let isMounted = true;

    const compileScript = async () => {
      try {
        setCompilerConfig(prev => ({ ...prev, isLoading: true, error: null }));

        // 1. TẢI ĐỘNG (LAZY-LOAD) BABEL VÀ TOÀN BỘ THƯ VIỆN MUI CÙNG LÚC
        const loadBabel = loadBabelOnce();
        const [Babel, DynamicallyLoadedMUI] = await Promise.all([
          loadBabel,
          import('@mui/material') // Tải động toàn bộ MUI (Sẽ tách thành chunk riêng)
        ]);

        // 2. PHÂN TÍCH SCRIPT CỦA USER VÀ KIỂM TRA TÍNH TƯƠNG THÍCH
        let scriptBody = script;
        if (!scriptBody.includes("return ")) {
          scriptBody = "return (\n<React.Fragment>\n" + scriptBody + "\n</React.Fragment>\n);";
        }

        // 3. BỌC SCRIPT THÀNH REACT FUNCTIONAL COMPONENT, BƠM ĐỦ ĐỒ NGHỀ CHO USER
        const wrappedScript =
          "(function UserCustomReportView(props) {\n" +
          "  const { MUI, React, Chart, ContainerGrid, data, rawData, columns, condition, context, report, moment, numeral } = props;\n" +
          "  " + scriptBody + "\n" +
          "})";

        // 5. DỊCH JSX + KHỞI TẠO COMPONENT (xem bienDichJsx: không ghép `return ${ma}`)
        const UserComponent = bienDichComponent(Babel, wrappedScript, ['React', 'MUI', 'ContainerGrid', 'Chart'], [React, DynamicallyLoadedMUI, ContainerGrid, Chart]);

        if (isMounted) {
          setCompilerConfig({
            Component: UserComponent,
            MUI: DynamicallyLoadedMUI,
            ContainerGrid,
            error: null,
            isLoading: false
          });
        }
      } catch (err) {
        console.error("[DynamicCustomReport] Lỗi biên dịch script:", err);
        if (isMounted) setCompilerConfig({ Component: null, MUI: null, error: err.message, isLoading: false });
      }
    };

    if (script) {
      compileScript();
    } else {
      if (isMounted) setCompilerConfig({ Component: null, MUI: null, error: "Không có mã script hiển thị.", isLoading: false });
    }

    return () => { isMounted = false; };
  }, [script]); // Chỉ biên dịch lại khi SCRIPT đổi; dữ liệu đổi thì chỉ cần render lại props

  if (CompilerConfig.isLoading) return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 3, justifyContent: 'center', flexGrow: 1 }}>
      <CircularProgress size={24} />
      <Typography color="text.secondary">Đang biên dịch giao diện tuỳ chỉnh...</Typography>
    </Box>
  );

  if (CompilerConfig.error) return (
    <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
      <strong>Lỗi biên dịch Code Giao diện:</strong> {CompilerConfig.error}
    </Alert>
  );

  const { Component, MUI } = CompilerConfig;
  if (!Component || !MUI) return null;

  // 6. RENDER COMPONENT VÀ TRUYỀN PROPS DỮ LIỆU
  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, bgcolor: 'background.default' }}>
      <ErrorBoundary>
        <Component
          data={data}
          rawData={rawData}
          columns={columns}
          condition={condition}
          context={context}
          report={report}
          MUI={MUI}
          ContainerGrid={ContainerGrid}
          React={React}
          Chart={Chart}
          moment={moment}
          numeral={numeral}
        />
      </ErrorBoundary>
    </Box>
  );
};

// Bọc lỗi RUNTIME (script biên dịch được nhưng chạy lỗi) để không làm sập cả trang báo cáo
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("[DynamicCustomReport][runtime]", error, info); }
  render() {
    if (this.state.error) {
      return (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <strong>Lỗi khi chạy Code Giao diện:</strong> {String(this.state.error.message || this.state.error)}
        </Alert>
      );
    }
    return this.props.children;
  }
}

export default DynamicCustomReport;
